/**
 * REEDOS Knowledge Base — search box.
 *
 * Small, focused component:
 * · Debounced query state (via useKbSearch hook).
 * · Enter key navigates to the full /kb/search results page.
 * · Live-dropdown of the top 5 matches for instant navigation.
 *
 * Analytics: the useKbSearch hook fires the search event on debounce.
 * On-click of a result the caller can log `kbRecordSearch` with the
 * `clicked_slug` for click-through-rate rollups.
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useKbSearch, kbRecordSearch } from "@/lib/kb";

export default function KBSearchBox({ autoFocus = false, placeholder = "Search the knowledge base…" }) {
    const [q, setQ] = useState("");
    const [openDropdown, setOpenDropdown] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const navigate = useNavigate();
    const { data, loading } = useKbSearch(q, { limit: 5 });

    // Click-outside → close dropdown.
    useEffect(() => {
        function handler(e) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) {
                setOpenDropdown(false);
            }
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const results = data?.results || [];

    function submit(e) {
        e.preventDefault();
        if (!q.trim()) return;
        navigate(`/kb/search?q=${encodeURIComponent(q.trim())}`);
        setOpenDropdown(false);
    }

    function handleResultClick(slug, kind) {
        kbRecordSearch({ query: q, results_count: results.length, clicked_slug: slug });
        setOpenDropdown(false);
        navigate(`/kb/${kind === "workflow" ? "workflow" : "article"}/${slug}`);
    }

    return (
        <form
            onSubmit={submit}
            role="search"
            ref={containerRef}
            className="kb-search-box relative w-full"
            data-testid="kb-search-form"
        >
            <div className="flex items-center gap-2 rounded-md border border-border bg-background focus-within:ring-2 focus-within:ring-primary/40">
                <Search className="ml-2 h-4 w-4 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    autoFocus={autoFocus}
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setOpenDropdown(true);
                    }}
                    onFocus={() => setOpenDropdown(true)}
                    placeholder={placeholder}
                    className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="kb-search-input"
                    aria-label="Search the knowledge base"
                />
                {q && (
                    <button
                        type="button"
                        onClick={() => {
                            setQ("");
                            inputRef.current?.focus();
                        }}
                        className="pr-2 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search"
                        data-testid="kb-search-clear"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {openDropdown && q.trim().length >= 2 && (
                <div
                    className="absolute z-40 mt-1 w-full rounded-md border border-border bg-background shadow-lg"
                    data-testid="kb-search-dropdown"
                >
                    {loading && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                            Searching…
                        </div>
                    )}
                    {!loading && results.length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                            No matching articles.
                        </div>
                    )}
                    {results.map((r) => (
                        <button
                            key={`${r.kind}-${r.slug}`}
                            type="button"
                            onClick={() => handleResultClick(r.slug, r.kind)}
                            className="block w-full text-left px-3 py-2 text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                            data-testid={`kb-search-result-${r.slug}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="truncate font-medium">{r.title}</span>
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                                    {r.kind}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                                {r.product} · {r.module || "—"}
                            </div>
                        </button>
                    ))}
                    {q.trim() && (
                        <button
                            type="submit"
                            className="block w-full text-left border-t border-border/60 px-3 py-2 text-xs text-primary hover:bg-muted"
                            data-testid="kb-search-see-all"
                        >
                            See all results for “{q.trim()}” →
                        </button>
                    )}
                </div>
            )}
        </form>
    );
}
